import { useEffect, useMemo, useState } from 'react'
import { FiMessageCircle, FiTag } from 'react-icons/fi'

import api from '../../utils/api'
import { formatDuracaoMinutos } from '../../utils/duration'
import { resolvePriceForDay, DIAS_SEMANA } from '../../utils/resolvePrice'
import useFlashMessage from '../../hooks/useFlashMessage'

import './Precos.css'

// Ordem de exibição começando na segunda (convenção BR) — diferente da
// ordem de DIAS_SEMANA (que começa domingo, pra bater com Date.getDay()).
const DIAS_EXIBIDOS = [
    { key: 'segunda', label: 'Segunda' },
    { key: 'terca', label: 'Terça' },
    { key: 'quarta', label: 'Quarta' },
    { key: 'quinta', label: 'Quinta' },
    { key: 'sexta', label: 'Sexta' },
    { key: 'sabado', label: 'Sábado' },
    { key: 'domingo', label: 'Domingo' },
]

const hojeKey = DIAS_SEMANA[new Date().getDay()]

// Dias com o preço igual (mesmo valor final E mesmo status de promoção em
// TODOS os tiers) viram uma linha só ("Segunda a Sexta" em vez de 5 linhas
// iguais) — só agrupa dias consecutivos na ordem de exibição, sem "dar a
// volta" (domingo não se junta com segunda mesmo que o preço bata).
function buildDiaRows(tiers) {
    const porDia = DIAS_EXIBIDOS.map((dia) => ({
        dia,
        cells: tiers.map((tier) => resolvePriceForDay(tier.price, tier.precosPorDia, tier.descontoPorDia, dia.key)),
    }))

    const assinatura = (cells) => cells.map((c) => `${c.precoFinal}:${c.temDesconto ? c.precoBase : ''}`).join('|')

    const grupos = []
    for (const entry of porDia) {
        const ultimo = grupos[grupos.length - 1]
        if (ultimo && assinatura(ultimo.cells) === assinatura(entry.cells)) {
            ultimo.dias.push(entry.dia)
        } else {
            grupos.push({ dias: [entry.dia], cells: entry.cells })
        }
    }

    return grupos.map((g) => ({
        key: g.dias.map((d) => d.key).join('-'),
        label: g.dias.length === 1 ? g.dias[0].label : `${g.dias[0].label} a ${g.dias[g.dias.length - 1].label}`,
        isHoje: g.dias.some((d) => d.key === hojeKey),
        cells: g.cells,
    }))
}

// Agrupa por duração (quantidadeTempos) — dentro de cada grupo, uma tabela
// dia × tamanho de grupo, em vez de um card por combo (mais fácil de
// comparar preço entre dias e entre tamanhos de grupo de uma vez).
function groupByDuracao(combos) {
    const grupos = new Map()
    for (const combo of combos) {
        const lista = grupos.get(combo.quantidadeTempos) || []
        lista.push(combo)
        grupos.set(combo.quantidadeTempos, lista)
    }
    return Array.from(grupos.entries())
        .sort(([a], [b]) => a - b)
        .map(([quantidadeTempos, tiersSemOrdenar]) => {
            const tiers = tiersSemOrdenar.sort((a, b) => a.minPessoas - b.minPessoas)
            return {
                quantidadeTempos,
                duracaoTempoMinutos: tiers[0].duracaoTempoMinutos,
                tiers,
                rows: buildDiaRows(tiers),
            }
        })
}

function Precos() {
    const [combos, setCombos] = useState(null)
    const [whatsapp, setWhatsapp] = useState(null)
    const { setFlashMessage } = useFlashMessage()

    useEffect(() => {
        api.get('/configuracao/combos').then((response) => {
            setCombos(response.data.combos || [])
        }).catch((error) => {
            setFlashMessage(error.response?.data?.message || 'Não foi possível carregar a tabela de preço', 'error')
            setCombos([])
        })

        api.get('/configuracao/contato').then((response) => {
            setWhatsapp(response.data.contato?.whatsapp || null)
        }).catch(() => setWhatsapp(null))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const grupos = useMemo(() => groupByDuracao(combos || []), [combos])

    return (
        <div className="precos">
            <p className="precos-eyebrow">Sobre a arena</p>
            <h1 className="precos-title">Preços</h1>
            <p className="precos-subtitle">Preço por dia da semana — quanto maior o grupo, melhor o preço.</p>

            {!combos && <p className="precos-empty">Carregando tabela de preço...</p>}
            {combos?.length === 0 && <p className="precos-empty">A tabela de preço ainda não foi cadastrada.</p>}

            {grupos.map((grupo) => (
                <div className="precos-grupo panel" key={grupo.quantidadeTempos}>
                    <h2 className="precos-grupo-title">
                        {formatDuracaoMinutos(grupo.quantidadeTempos * grupo.duracaoTempoMinutos)} de jogo
                    </h2>

                    <div className="precos-table-wrap">
                        <table className="precos-table">
                            <thead>
                                <tr>
                                    <th>Dia</th>
                                    {grupo.tiers.map((tier) => (
                                        <th key={tier._id}>a partir de {tier.minPessoas}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {grupo.rows.map((row) => (
                                    <tr key={row.key} className={row.isHoje ? 'hoje' : ''}>
                                        <td className="precos-dia">{row.label}{row.isHoje && <span className="precos-hoje-tag">hoje</span>}</td>
                                        {row.cells.map((cell, index) => (
                                            <td key={grupo.tiers[index]._id} className="precos-cell">
                                                {cell.temDesconto && <span className="precos-cell-old">R$ {cell.precoBase.toFixed(2)}</span>}
                                                <span className={`precos-cell-value${cell.temDesconto ? ' promo' : ''}`}>
                                                    R$ {cell.precoFinal.toFixed(2)}
                                                </span>
                                                <span className="precos-cell-caption">por pessoa</span>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {whatsapp && (
                <a className="btn-primary precos-whatsapp" href={`https://wa.me/55${whatsapp}`} target="_blank" rel="noreferrer">
                    <FiMessageCircle size={16} />
                    Agendar pelo WhatsApp
                </a>
            )}

            <p className="precos-legend">
                <FiTag size={13} />
                Valores com desconto de promoção aparecem em destaque, com o preço original riscado.
            </p>
        </div>
    )
}

export default Precos
